import { useMutation } from '@apollo/client';
import { useCurrentUser } from '@cosuno/authentication-frontend';
import { Clickable, COLOR, Icon, Label } from '@cosuno/cosuno-ui';
import { useFeatureFlags } from '@cosuno/feature-flags-frontend';
import { formatDate } from '@cosuno/localization';
import moment from 'moment';
import React, { memo } from 'react';

import { toast } from '~/packages/Toast';
import useAnalytics from '~/shared/hooks/useAnalytics';
import useTranslation from '~/shared/hooks/useTranslation';

import { PromptBar, PromptButton, TextWrapper } from '../Styles';
import { mutationRequestContractExtension } from './api';

const ContractEndingPrompt: React.FC = () => {
  const { t } = useTranslation('contractEndingPrompt');
  const { trackEvent } = useAnalytics();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();

  const [requestContractExtension] = useMutation(mutationRequestContractExtension);

  if (!currentUser) return null;

  if (featureFlags['default-contract-ending-warning-2'] === false) return null;

  if (currentUser.company.contractEndDate === null) return null;

  const daysUntilEndDate = Math.abs(moment().diff(currentUser.company.contractEndDate, 'd'));

  if (daysUntilEndDate > 30) return null;

  return (
    <PromptBar
      as={Clickable}
      data-cy-contract-ending-prompt
      onClick={async () => {
        trackEvent('contractExtend', { userType: currentUser.type });
        try {
          await requestContractExtension();
          toast.success(t('common:upsells.successToast'));
        } catch (error) {
          toast.error(error);
        }
      }}
      $color={COLOR.highlightNeon}
    >
      <TextWrapper>
        <Icon type="info" color={COLOR.white} size={20} />
        <Label variant="large" color={COLOR.white}>
          {t('message', { date: formatDate(currentUser.company.contractEndDate) })}
        </Label>
      </TextWrapper>
      <PromptButton size="small">{t('buttonLabel')}</PromptButton>
    </PromptBar>
  );
};

export default memo(ContractEndingPrompt);
